import { queryClient } from '@app/providers/query';

import {
  adminNavigationItems,
  clientNavigationItems,
  filterNavigationItems,
  type NavItem,
} from '@widgets/layout';

import { myPermissionsQueryOptions } from '@entities/client/permission';
import { accountQueryOptions, isAdmin, PrincipalType } from '@entities/shared/account';

// First navigable path in a (permission-filtered) nav tree. A group has no own path,
// so descend into its children until a leaf path is found.
const firstNavPath = (items: NavItem[]): string | null => {
  for (const item of items) {
    if (item.path) return item.path;

    if (item.children) {
      const childPath = firstNavPath(item.children);
      if (childPath) return childPath;
    }
  }

  return null;
};

// Resolve the principal's reachable home — the top of its own sidebar. Derived from the
// SAME nav config + permission filter the layout renders (`@widgets/layout`), so the home
// is literally the first menu item the principal can see; no duplicated resource→path map.
// Falls back to the ungated '/no-access' when nothing is permitted. Used by both the bucket
// (principal-type) and segment (permission) gates so a denied route redirects only to
// somewhere the principal can actually land — never a gated page that bounces back and
// freezes the tab in a redirect loop.
export const resolveHomePath = async (): Promise<string> => {
  const account = await queryClient.ensureQueryData(accountQueryOptions());

  if (account.principalType === PrincipalType.Machine) return '/login';

  const permissions = await queryClient.ensureQueryData(myPermissionsQueryOptions());
  const navItems = isAdmin(account) ? adminNavigationItems : clientNavigationItems;

  return firstNavPath(filterNavigationItems(navItems, permissions)) ?? '/no-access';
};
